import { Alert, Button, Card, Progress, Spin, Typography } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useIsMobileView } from "../../hooks/ui-hook";
import {
  type SetupPortal as Portal,
  type StepKey,
  isGated,
  primePortalCsrf,
  useGetSetupPortalQuery,
  useVerifySetupAccessMutation,
} from "../../service/onboarding.service";
import AccessGate from "./AccessGate.component";
import BaaStep from "./BaaStep.component";
import EhrLoginForm from "./EhrLoginForm.component";
import GoLiveStep from "./GoLiveStep.component";
import GroupAdminForm from "./GroupAdminForm.component";
import SetupSteps from "./SetupSteps.component";
import styles from "./setup.module.scss";

const { Text, Title } = Typography;

/** Banner kind onto Ant's Alert types, so it renders in the app's own styling. */
const BANNER_TYPE = {
  action_needed: "warning",
  nudge_working: "info",
  complete: "success",
} as const;

/** The customer-facing onboarding portal. */
export default function SetupPortalPage() {
  const { token = "" } = useParams<{ token: string }>();
  const isMobile = useIsMobileView();
  const [searchParams, setSearchParams] = useSearchParams();
  // index.html moves ?access= off the URL before any analytics tag loads and parks it here.
  const [accessToken] = useState(() => {
    // Read once: the grant is single-use, so a remount must not retry it.
    const w = window as { __NUDGE_PORTAL_ACCESS__?: string };
    const token = w.__NUDGE_PORTAL_ACCESS__ ?? null;
    w.__NUDGE_PORTAL_ACCESS__ = undefined;
    return token;
  });
  // DocuSign returns the customer here after signing.
  const returnedFromSigning = searchParams.get("baa") === "returned";

  const [verify, { isLoading: isVerifying }] = useVerifySetupAccessMutation();
  const [linkError, setLinkError] = useState<string | undefined>();
  const [openStep, setOpenStep] = useState<StepKey | null>(null);
  const [hasTriedLink, setHasTriedLink] = useState(!accessToken);

  // Not before the magic link is redeemed: that first GET could only return the gate.
  const { data, isLoading, isError, refetch } = useGetSetupPortalQuery(token, {
    skip: !token || !hasTriedLink,
  });

  // Seed the CSRF token first: every POST is csrf_protect'd and the customer never hit /login.
  const csrfReadyRef = useRef<Promise<void> | null>(null);
  if (csrfReadyRef.current === null) {
    csrfReadyRef.current = primePortalCsrf();
  }

  // Redeem the magic link once; the grant is single-use, so a re-run would burn it.
  const redeemedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!accessToken || !token) return;
    if (redeemedRef.current === accessToken) return;
    redeemedRef.current = accessToken;
    let cancelled = false;
    (async () => {
      try {
        // Wait for the CSRF cookie, or this POST 403s and reads as an expired link.
        await csrfReadyRef.current;
        await verify({ token, accessToken }).unwrap();
      } catch {
        if (!cancelled) {
          setLinkError("That link has expired or has already been used. Enter your email for a fresh one.");
        }
      } finally {
        if (!cancelled) setHasTriedLink(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken, token, verify]);

  // Strip ?baa= once handled, so a refresh doesn't re-run the confirmation.
  const clearSigningReturn = useCallback(() => {
    const next = new URLSearchParams(window.location.search);
    next.delete("baa");
    setSearchParams(next, { replace: true });
  }, [setSearchParams]);

  const portal = !isGated(data) ? (data as Portal | undefined) : undefined;

  // Open whichever step needs the customer, and follow it as steps complete.
  const focusStep = portal?.banner.step_key ?? portal?.progress.current_step_key ?? null;
  useEffect(() => {
    if (focusStep) setOpenStep(focusStep);
  }, [focusStep]);

  const stepBody = useMemo(
    () => ({
      baa: portal ? (
        <BaaStep
          token={token}
          portal={portal}
          justReturned={returnedFromSigning}
          onReturnHandled={clearSigningReturn}
        />
      ) : null,
      group_admin: portal ? (
        <GroupAdminForm
          token={token}
          isComplete={!!portal.steps.find((s) => s.key === "group_admin" && s.status === "complete")}
        />
      ) : null,
      ehr_login: portal ? <EhrLoginForm token={token} portal={portal} /> : null,
      templates: null,
      ai_assistant: null,
      go_live: portal ? <GoLiveStep token={token} portal={portal} /> : null,
    }),
    [portal, token, returnedFromSigning, clearSigningReturn],
  );

  if (!token) {
    return (
      <div className={styles.body}>
        <div className={styles.shell}>
          <Alert
            type="error"
            showIcon
            message="This onboarding link is not valid. Enter your email below and we'll send you a fresh one."
          />
        </div>
      </div>
    );
  }

  if ((isLoading || isVerifying || !hasTriedLink) && !portal) {
    return (
      <div className={styles.body}>
        <div className={styles.centered}>
          <Spin size="large" />
          <span>Loading your onboarding portal…</span>
        </div>
      </div>
    );
  }

  if (isError && !data) {
    return (
      <div className={styles.body}>
        <div className={styles.shell}>
          <Alert
            type="error"
            showIcon
            message="We couldn't open your onboarding portal"
            description="The link may be out of date. Try again, or get in touch and we'll send a fresh one."
            action={<Button onClick={() => refetch()}>Retry</Button>}
          />
        </div>
      </div>
    );
  }

  if (!portal) {
    return (
      <div className={styles.body}>
        <AccessGate token={token} groupName={isGated(data) ? data.group_name : ""} linkError={linkError} />
      </div>
    );
  }

  const { banner, progress } = portal;

  return (
    <div className={styles.body}>
      <div className={styles.shell}>
        <Alert
          className={styles.banner}
          type={BANNER_TYPE[banner.kind]}
          showIcon
          message={<span className={styles.bannerHeadline}>{banner.headline}</span>}
          description={<span className={styles.bannerBody}>{banner.body}</span>}
          action={
            banner.step_key ? (
              <Button
                type="primary"
                size={isMobile ? "middle" : "small"}
                block={isMobile}
                onClick={() => {
                  setOpenStep(banner.step_key);
                  document.getElementById("setup-steps")?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
              >
                Go to this step
              </Button>
            ) : null
          }
        />

        <Card
          className={styles.card}
          variant="borderless"
          styles={{ body: { padding: isMobile ? "16px 12px 4px" : "22px 24px 8px" } }}
        >
          <div className={styles.cardHead}>
            <Title level={5} className={styles.groupName}>
              {portal.group_name}
            </Title>
            <Text type="secondary" className={styles.progressLabel}>
              {progress.is_complete
                ? "All steps complete"
                : `Step ${progress.current_position} of ${progress.total} \u00b7 ${progress.completed} done`}
            </Text>
          </div>

          <Progress
            percent={Math.round((progress.completed / Math.max(progress.total, 1)) * 100)}
            showInfo={false}
            strokeColor={progress.is_complete ? "#10b981" : "#6366F1"}
            trailColor="#EEF2FF"
            size="small"
            className={styles.progressBar}
          />

          <div id="setup-steps">
            <SetupSteps
              steps={portal.steps}
              openStep={openStep}
              onToggle={(key) => setOpenStep(openStep === key ? null : key)}
              bodies={stepBody}
            />
          </div>
        </Card>

        {/* No support address here: a blocked step names what to fix and reopens the form. */}
        <div className={styles.help}>
          <span className={styles.viewer}>Signed in as {portal.viewer_email}</span>
        </div>
      </div>
    </div>
  );
}
